import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import webhookSubmissionService from '../../../services/webhookSubmissionService';

const ApplicationForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    // Step 1: Basic Information
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    
    // Step 2: License & Trade Information
    licenseNumber: '',
    licenseType: '',
    tradeSpecialty: '',
    yearsExperience: '',
    insuranceProvider: '',
    
    // Step 3: Business Details
    serviceAreas: [],
    currentRevenue: '',
    marketingSpend: '',
    businessChallenges: [],
    
    // Step 4: Availability & Preferences
    availability: '',
    preferredJobTypes: [],
    teamSize: '',
    agreeToTerms: false,
    agreeToBackground: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedProgress, setSavedProgress] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const steps = [
    {
      title: "Basic Information",
      description: "Tell us about yourself and your business",
      icon: "User"
    },
    {
      title: "License & Trade Details",
      description: "Verify your professional credentials",
      icon: "FileText"
    },
    {
      title: "Business Information",
      description: "Help us understand your current situation",
      icon: "Building"
    },
    {
      title: "Final Details",
      description: "Complete your founding contractor application",
      icon: "CheckCircle"
    }
  ];

  const tradeOptions = [
    { value: 'hvac', label: 'HVAC Specialist' },
    { value: 'plumbing', label: 'Licensed Plumber' },
    { value: 'electrical', label: 'Licensed Electrician' },
    { value: 'multi-trade', label: 'Multi-Trade Contractor' }
  ];

  const serviceAreaOptions = [
    { value: 'tampa', label: 'Tampa' },
    { value: 'st-petersburg', label: 'St. Petersburg' },
    { value: 'clearwater', label: 'Clearwater' },
    { value: 'brandon', label: 'Brandon' },
    { value: 'plant-city', label: 'Plant City' },
    { value: 'wesley-chapel', label: 'Wesley Chapel' },
    { value: 'riverview', label: 'Riverview' },
    { value: 'lutz', label: 'Lutz' }
  ];

  const challengeOptions = [
    { value: 'expensive-marketing', label: 'Expensive marketing with poor ROI' },
    { value: 'payment-delays', label: 'Long payment delays (30-90 days)' },
    { value: 'low-quality-leads', label: 'Low-quality leads and price shoppers' },
    { value: 'admin-overhead', label: 'Too much administrative work' },
    { value: 'seasonal-fluctuations', label: 'Seasonal revenue fluctuations' },
    { value: 'competition', label: 'Intense price competition' }
  ];

  const jobTypeOptions = [
    { value: 'residential-service', label: 'Residential Service Calls' },
    { value: 'residential-installation', label: 'Residential Installations' },
    { value: 'commercial-service', label: 'Commercial Service' },
    { value: 'commercial-installation', label: 'Commercial Installations' },
    { value: 'emergency-service', label: 'Emergency Services' },
    { value: 'maintenance-contracts', label: 'Maintenance Contracts' }
  ];

  // Auto-save functionality
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.keys(formData)?.some(key => formData?.[key] !== '')) {
        localStorage.setItem('proconnect-application', JSON.stringify(formData));
        setSavedProgress(true);
        setTimeout(() => setSavedProgress(false), 2000);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [formData]);

  // Load saved progress
  useEffect(() => {
    const saved = localStorage.getItem('proconnect-application');
    if (saved) {
      setFormData(JSON.parse(saved));
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Clear any previous submit errors
    if (submitError) {
      setSubmitError('');
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0:
        if (!formData?.fullName?.trim()) newErrors.fullName = 'Full name is required';
        if (!formData?.email?.trim()) newErrors.email = 'Email is required';
        if (!/\S+@\S+\.\S+/?.test(formData?.email)) newErrors.email = 'Valid email is required';
        if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
        if (!formData?.companyName?.trim()) newErrors.companyName = 'Company name is required';
        break;
      
      case 1:
        if (!formData?.licenseNumber?.trim()) newErrors.licenseNumber = 'License number is required';
        if (!formData?.licenseType) newErrors.licenseType = 'License type is required';
        if (!formData?.tradeSpecialty) newErrors.tradeSpecialty = 'Trade specialty is required';
        if (!formData?.yearsExperience) newErrors.yearsExperience = 'Years of experience is required';
        break;
      
      case 2:
        if (formData?.serviceAreas?.length === 0) newErrors.serviceAreas = 'Select at least one service area';
        if (!formData?.currentRevenue) newErrors.currentRevenue = 'Current revenue range is required';
        break;
      
      case 3:
        if (!formData?.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms';
        if (!formData?.agreeToBackground) newErrors.agreeToBackground = 'Background check consent is required';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps?.length - 1));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  // Updated handleSubmit with webhookSubmissionService instead of contractorApplicationService
  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Add UTM and referrer data if available
      const submissionData = {
        ...formData,
        utmSource: new URLSearchParams(window?.location?.search)?.get('utm_source'),
        utmMedium: new URLSearchParams(window?.location?.search)?.get('utm_medium'),
        utmCampaign: new URLSearchParams(window?.location?.search)?.get('utm_campaign'),
        referrer: document?.referrer || window?.location?.origin
      };

      // Use webhookSubmissionService instead of contractorApplicationService
      const { data, error } = await webhookSubmissionService?.submitApplicationForm(submissionData);

      if (error) {
        setSubmitError(error?.message || 'Failed to submit application. Please try again.');
        return;
      }

      // Clear saved progress on successful submission
      localStorage.removeItem('proconnect-application');
      
      setSubmitSuccess(true);
      
      // Optional: Show success for a few seconds then reset form
      setTimeout(() => {
        setSubmitSuccess(false);
        setCurrentStep(0);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          companyName: '',
          licenseNumber: '',
          licenseType: '',
          tradeSpecialty: '',
          yearsExperience: '',
          insuranceProvider: '',
          serviceAreas: [],
          currentRevenue: '',
          marketingSpend: '',
          businessChallenges: [],
          availability: '',
          preferredJobTypes: [],
          teamSize: '',
          agreeToTerms: false,
          agreeToBackground: false
        });
      }, 5000);

    } catch (error) {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const progressPercentage = ((currentStep + 1) / steps?.length) * 100;

  // Success state display
  if (submitSuccess) {
    return (
      <section id="early-access" className="py-16 lg:py-24 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="bg-card rounded-xl border border-border shadow-testimonial p-12">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                  <Icon name="CheckCircle" size={40} className="text-green-600" />
                </div>
              </div>
              
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Application Submitted Successfully!
              </h2>
              
              <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
                Thank you for applying to become a ProConnect founding contractor. 
                We have received your application and will review it within 24 hours.
              </p>
              
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-foreground mb-2">What happens next?</h4>
                <ul className="text-sm text-muted-foreground text-left max-w-md mx-auto space-y-1">
                  <li>• Application review (1-2 business days)</li>
                  <li>• Background and license verification</li>
                  <li>• Interview scheduling (if approved)</li>
                  <li>• Onboarding and platform access</li>
                </ul>
              </div>
              
              <p className="text-sm text-muted-foreground">
                You will receive email updates at <strong>{formData?.email}</strong>
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="early-access" className="py-16 lg:py-24 bg-muted/30">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
            Apply for Founding Contractor Status
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Join the exclusive group of Tampa Bay contractors earning $13,000+ more annually. 
            Limited to 235 founding members.
          </p>
        </div>

        <div className="bg-card rounded-xl border border-border shadow-testimonial overflow-hidden">
          {/* Progress Header */}
          <div className="bg-muted/50 px-8 py-6 border-b border-border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                {steps?.[currentStep]?.title}
              </h3>
              {savedProgress && (
                <div className="flex items-center text-sm text-accent">
                  <Icon name="Check" size={16} className="mr-1" />
                  Progress Saved
                </div>
              )}
            </div>
            
            <p className="text-muted-foreground mb-4">{steps?.[currentStep]?.description}</p>
            
            {/* Progress Bar */}
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            
            <div className="flex justify-between text-sm text-muted-foreground mt-2">
              <span>Step {currentStep + 1} of {steps?.length}</span>
              <span>{Math.round(progressPercentage)}% Complete</span>
            </div>
          </div>

          {/* Error Display */}
          {submitError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-8 py-4">
              <div className="flex items-center">
                <Icon name="AlertCircle" size={16} className="mr-2" />
                <span>{submitError}</span>
              </div>
            </div>
          )}

          {/* Form Content */}
          <div className="p-8">
            {/* Step 1: Basic Information */}
            {currentStep === 0 && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <Input
                    label="Full Name"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData?.fullName}
                    onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                    error={errors?.fullName}
                    required
                  />
                  
                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="your.email@example.com"
                    value={formData?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    error={errors?.email}
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <Input
                    label="Phone Number"
                    type="tel"
                    placeholder="(813) 555-0123"
                    value={formData?.phone}
                    onChange={(e) => handleInputChange('phone', e?.target?.value)}
                    error={errors?.phone}
                    required
                  />
                  
                  <Input
                    label="Company Name"
                    type="text"
                    placeholder="Your Company LLC"
                    value={formData?.companyName}
                    onChange={(e) => handleInputChange('companyName', e?.target?.value)}
                    error={errors?.companyName}
                    required
                  />
                </div>
              </div>
            )}

            {/* Step 2: License & Trade Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <Input
                    label="Florida License Number"
                    type="text"
                    placeholder="e.g., CAC1234567"
                    value={formData?.licenseNumber}
                    onChange={(e) => handleInputChange('licenseNumber', e?.target?.value)}
                    error={errors?.licenseNumber}
                    required
                  />
                  
                  <Select
                    label="License Type"
                    placeholder="Select license type"
                    options={[
                      { value: 'hvac-contractor', label: 'HVAC Contractor' },
                      { value: 'plumbing-contractor', label: 'Plumbing Contractor' },
                      { value: 'electrical-contractor', label: 'Electrical Contractor' },
                      { value: 'general-contractor', label: 'General Contractor' }
                    ]}
                    value={formData?.licenseType}
                    onChange={(value) => handleInputChange('licenseType', value)}
                    error={errors?.licenseType}
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <Select
                    label="Primary Trade Specialty"
                    placeholder="Select your specialty"
                    options={tradeOptions}
                    value={formData?.tradeSpecialty}
                    onChange={(value) => handleInputChange('tradeSpecialty', value)}
                    error={errors?.tradeSpecialty}
                    required
                  />
                  
                  <Select
                    label="Years of Experience"
                    placeholder="Select experience level"
                    options={[
                      { value: '1-3', label: '1-3 years' },
                      { value: '4-7', label: '4-7 years' },
                      { value: '8-15', label: '8-15 years' },
                      { value: '15+', label: '15+ years' }
                    ]}
                    value={formData?.yearsExperience}
                    onChange={(value) => handleInputChange('yearsExperience', value)}
                    error={errors?.yearsExperience}
                    required
                  />
                </div>

                <Input
                  label="Insurance Provider"
                  type="text"
                  placeholder="e.g., State Farm, Allstate"
                  value={formData?.insuranceProvider}
                  onChange={(e) => handleInputChange('insuranceProvider', e?.target?.value)}
                  description="General liability insurance provider"
                />
              </div>
            )}

            {/* Step 3: Business Information */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <Select
                  label="Service Areas"
                  placeholder="Select areas you serve"
                  options={serviceAreaOptions}
                  value={formData?.serviceAreas}
                  onChange={(value) => handleInputChange('serviceAreas', value)}
                  error={errors?.serviceAreas}
                  multiple
                  searchable
                  required
                />

                <div className="grid md:grid-cols-2 gap-6">
                  <Select
                    label="Current Annual Revenue"
                    placeholder="Select revenue range"
                    options={[
                      { value: '50k-75k', label: '$50,000 - $75,000' },
                      { value: '75k-100k', label: '$75,000 - $100,000' },
                      { value: '100k-150k', label: '$100,000 - $150,000' },
                      { value: '150k-200k', label: '$150,000 - $200,000' },
                      { value: '200k+', label: '$200,000+' }
                    ]}
                    value={formData?.currentRevenue}
                    onChange={(value) => handleInputChange('currentRevenue', value)}
                    error={errors?.currentRevenue}
                    required
                  />
                  
                  <Select
                    label="Monthly Marketing Spend"
                    placeholder="Select spending range"
                    options={[
                      { value: '0-500', label: '$0 - $500' },
                      { value: '500-1500', label: '$500 - $1,500' },
                      { value: '1500-3000', label: '$1,500 - $3,000' },
                      { value: '3000-5000', label: '$3,000 - $5,000' },
                      { value: '5000+', label: '$5,000+' }
                    ]}
                    value={formData?.marketingSpend}
                    onChange={(value) => handleInputChange('marketingSpend', value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Current Business Challenges (Select all that apply)
                  </label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {challengeOptions?.map((option) => (
                      <Checkbox
                        key={option?.value}
                        label={option?.label}
                        checked={formData?.businessChallenges?.includes(option?.value)}
                        onChange={(e) => {
                          const challenges = e?.target?.checked
                            ? [...formData?.businessChallenges, option?.value]
                            : formData?.businessChallenges?.filter(c => c !== option?.value);
                          handleInputChange('businessChallenges', challenges);
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Final Details */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <Select
                    label="Availability to Start"
                    placeholder="When can you start?"
                    options={[
                      { value: 'immediately', label: 'Immediately' },
                      { value: '1-2-weeks', label: '1-2 weeks' },
                      { value: '3-4-weeks', label: '3-4 weeks' },
                      { value: '1-2-months', label: '1-2 months' }
                    ]}
                    value={formData?.availability}
                    onChange={(value) => handleInputChange('availability', value)}
                  />
                  
                  <Select
                    label="Team Size"
                    placeholder="Select team size"
                    options={[
                      { value: 'solo', label: 'Solo contractor' },
                      { value: '2-3', label: '2-3 people' },
                      { value: '4-6', label: '4-6 people' },
                      { value: '7+', label: '7+ people' }
                    ]}
                    value={formData?.teamSize}
                    onChange={(value) => handleInputChange('teamSize', value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Preferred Job Types (Select all that apply)
                  </label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {jobTypeOptions?.map((option) => (
                      <Checkbox
                        key={option?.value}
                        label={option?.label}
                        checked={formData?.preferredJobTypes?.includes(option?.value)}
                        onChange={(e) => {
                          const jobTypes = e?.target?.checked
                            ? [...formData?.preferredJobTypes, option?.value]
                            : formData?.preferredJobTypes?.filter(t => t !== option?.value);
                          handleInputChange('preferredJobTypes', jobTypes);
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* Terms and Agreements */}
                <div className="space-y-4 pt-6 border-t border-border">
                  <Checkbox
                    label="I agree to ProConnect's Terms of Service and Privacy Policy"
                    checked={formData?.agreeToTerms}
                    onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
                    error={errors?.agreeToTerms}
                    required
                  />
                  
                  <Checkbox
                    label="I consent to background and license verification checks"
                    checked={formData?.agreeToBackground}
                    onChange={(e) => handleInputChange('agreeToBackground', e?.target?.checked)}
                    error={errors?.agreeToBackground}
                    required
                  />
                </div>

                {/* Application Summary */}
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                  <h4 className="font-semibold text-foreground mb-4">Application Summary</h4>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Name:</span>
                      <span className="ml-2 text-foreground">{formData?.fullName}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Trade:</span>
                      <span className="ml-2 text-foreground">
                        {tradeOptions?.find(t => t?.value === formData?.tradeSpecialty)?.label}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">License:</span>
                      <span className="ml-2 text-foreground">{formData?.licenseNumber}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Experience:</span>
                      <span className="ml-2 text-foreground">{formData?.yearsExperience} years</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form Navigation */}
          <div className="bg-muted/50 px-8 py-6 border-t border-border">
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 0}
                iconName="ChevronLeft"
                iconPosition="left"
              >
                Previous
              </Button>
              
              {currentStep < steps?.length - 1 ? (
                <Button
                  variant="default"
                  onClick={handleNext}
                  iconName="ChevronRight"
                  iconPosition="right"
                >
                  Next Step
                </Button>
              ) : (
                <Button
                  variant="default"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  iconName="Send"
                  iconPosition="left"
                  className="bg-accent hover:bg-accent/90"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center space-x-8 text-sm text-muted-foreground">
            <div className="flex items-center">
              <Icon name="Shield" size={16} className="mr-2 text-accent" />
              Secure Application
            </div>
            <div className="flex items-center">
              <Icon name="Clock" size={16} className="mr-2 text-primary" />
              24-Hour Response
            </div>
            <div className="flex items-center">
              <Icon name="CheckCircle" size={16} className="mr-2 text-secondary" />
              No Commitment Required
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApplicationForm;